/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";
import { apiClient } from "./ApiClient";
import { checkDownloadLink } from "./downloadCheck";
import { DEFAULT_TIMEOUT } from "./cache";
import { configuration } from "@/lib/configuration";
import { authClient } from "@/lib/auth-client";
import { getForumUrl } from "@/utils/siteConfig";
import type {
  ApiClientOptions,
  FormApiResponse,
  ShoppingCartStatus,
  CameraRequestData,
  ContactUsData,
  LostLicenseData,
  MailProductLinkData,
  ReviewSubmissionData,
  ProductSupportData,
} from "./types";

/**
 * Normalize a phone number to E.164 format (e.g., +919898989998).
 * react-phone-input-2 stores digits only without the leading '+'.
 * Django's phonenumbers.parse() requires E.164 — validation fails without it.
 */
/**
 * Session 519 review, N-4: a catalogue number the Feedback Management System
 * can match -- a whole number above zero, as its own lookup requires (it
 * ignores anything else). Not 0, a fraction, NaN, null or undefined.
 */
const isCatalogueNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const toE164 = (phone: string): string => {
  if (!phone) return phone;
  const t = phone.trim();
  return t.startsWith("+") ? t : `+${t.replace(/\D/g, "")}`;
};

export const api = {
  // Product data (i18n endpoints)
  products: {
    getAll: () => apiClient.get<Record<string, unknown>>("product/"),
    getBySlug: <T = unknown>(slug: string, options?: ApiClientOptions) =>
      apiClient.get<T>(`product/${slug}/`, options),
    getHeader: <T = unknown>(options?: ApiClientOptions) =>
      apiClient.get<T>("product-header/", options),
    /**
     * The part of the support product list the Product table cannot hold — the
     * Android applications. Read from the server rather than kept here, because
     * the server validates what the support forms submit (IND-10); two copies
     * would drift and a customer choosing a product this site offered would be
     * refused on submit.
     */
    getSupportCatalogue: <T = unknown>(options?: ApiClientOptions) =>
      apiClient.get<T>("support-catalogue/", options),
    getHomepage: <T = unknown>() => apiClient.get<T>("homepage/"),
    getDownload: <T = unknown>() => apiClient.get<T>("download/"),
    // Session 507 review, VM-1 (src/api/downloadCheck.ts).
    checkDownload: checkDownloadLink,
    // F-13: one About Us endpoint. The mobile variant returned the same
    // products grouped into rows of two rather than three, and the page
    // flattened both before rendering — so it cost a second request on every
    // phone rotation for a difference nothing could see. Removed with the view.
    getAboutUs: <T = unknown>() => apiClient.get<T>("about-us/"),
    getVideoTutorials: <T = unknown>() =>
      apiClient.get<T>("video-tutorial-manual/"),
    // V-6: the Articles page must reflect an admin change immediately. A
    // product set inactive stayed on the page for up to five minutes because
    // the response was served from the shared cache, and a page refresh
    // reloads the page, not the stored copy. forceRefresh skips the cache read
    // and still writes the fresh response back, so the shared copy is renewed
    // for everyone rather than deleted. The response is small enough to fetch
    // this way since IND-25 removed the article bodies from it.
    getArticles: <T = unknown>() =>
      apiClient.get<T>("allarticles/", { forceRefresh: true }),
    getArticleBySlug: <T = unknown>(slug: string, options?: ApiClientOptions) =>
      apiClient.get<T>(`allarticles/${slug}/`, options),
    // Product subresource endpoints (slug-scoped, i18n)
    getVersions: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/version/`),
    getReviews: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/review/`),
    getCart: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/cart/`),
    getUserManual: <T = unknown>(slug: string, options?: ApiClientOptions) =>
      apiClient.get<T>(`product/${slug}/user_manual/`, options),
  },

  // Form submissions (non-i18n endpoints)
  forms: {
    submitCameraRequest: async (
      data: CameraRequestData,
    ): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/camera-request-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone ?? "",
          email: data.email,
          camera_brand: data.cameraBrand,
          camera_model: data.cameraModel,
          ipaddress: data.cameraIP,
          port_number: data.portNumber,
          camera_username: data.username ?? "",
          camera_password: data.password ?? "",
          camera_instruct: data.instruction ?? "",
        },
      );
      return { message: response.message, status: 200 };
    },

    submitContactUs: async (data: ContactUsData): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/contactus-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          message: data.message,
          phone_number: data.contactNumber ?? "",
          language: data.language ?? "en", // Forwarded for i18n routing and analytics
          // B2: "date" field removed — the back-end required it but immediately
          // discarded it in favour of server-generated time. Sending a value that
          // is never read is wasteful and confusing; removed from both sides.
        },
      );
      return { message: response.message, status: 200 };
    },

    submitLostLicense: async (
      data: LostLicenseData,
    ): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/lost-license-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          product_name: data.application,
          approx_date_purchase: data.formattedDate,
          // S-2: react-phone-input-2 stores digits without '+' (e.g., "919898989998").
          // Django phonenumbers.parse() requires E.164 format (+919898989998).
          phone_number: toE164(data.phone ?? ""),
          // DD-6: Forward deduplication token so the backend can reject duplicate taps.
          // Without these fields, request.data.get('submissionId') returns None and the
          // 5-second server-side dedup cache is bypassed entirely on every request.
          ...(data.submissionId ? { submissionId: data.submissionId } : {}),
          ...(data.clientTimestamp
            ? { clientTimestamp: data.clientTimestamp }
            : {}),
        },
      );
      return { message: response.message, status: 200 };
    },

    submitProductSupport: async (
      data: ProductSupportData,
    ): Promise<FormApiResponse> => {
      const formData = new FormData();
      formData.append("productHelpInquiry", data.productHelpInquiry);
      formData.append("type", data.type);
      formData.append("message", data.message);
      formData.append("email", data.email);
      if (data.product) formData.append("product", data.product);
      if (data.image) formData.append("image", data.image, "screenshot.png");
      if (data.accountOrderId)
        formData.append("accountOrderId", data.accountOrderId);
      if (data.company) formData.append("company", data.company);

      const response = await apiClient.postFormData<{ message: string }>(
        "api/support-form/",
        formData,
      );
      return { message: response.message, status: 200 };
    },

    sendProductLink: async (
      data: MailProductLinkData,
    ): Promise<FormApiResponse> => {
      // V-4: Send product_slug so the backend resolves the download URL from
      // the database rather than trusting the client. The backend no longer
      // accepts product_link as an authoritative URL — it looked up the link
      // from the route-slug instead, fixing the host-rewrite bug (V-4).
      const response = await apiClient.postDirect<{ message: string }>(
        "api/send_product_link/",
        {
          email: data.email,
          product_slug: data.productSlug,
        },
      );
      return { message: response.message, status: 200 };
    },

    // V-1 fix: Route website reviews to the FMS at the correct REST endpoint.
    // The previous path (/api/feedback/submit-review) no longer exists on the
    // FMS; the canonical route is /api/feedback/review/ (noun resource,
    // trailing slash per Django convention).
    //
    // Field changes from the FCR:
    //   reviewer_name  → user_name       (FMS serializer field)
    //   review / review_title → comment  (title folded in so nothing is lost)
    //   language       → app_language    (FMS serializer field)
    //   product_slug   → sent in both product_id and product_slug fields;
    //                    FMS resolves the product from whichever it reads
    //
    // A-1: Trailing slash is explicit — Django 301-redirects POST without it,
    //      which may drop the request body in some browsers.
    // A-2: AbortController gives the call a 15s timeout; without it a stalled
    //      FMS leaves the form spinner running indefinitely.
    // A-6: Sentry captures any review submission failure; the raw fetch bypasses
    //      the shared ApiClient that would otherwise report errors automatically.
    submitReview: async (
      data: ReviewSubmissionData,
    ): Promise<FormApiResponse> => {
      const base = configuration.feedbackAPIURL;
      if (!base) throw new Error("Review submission is not configured.");

      // Fold the review title into the comment body so it is never silently
      // dropped — the FMS intake uses a single `comment` field.
      const reviewTitle = data.reviewTitle.trim();
      const reviewBody = data.review.trim();
      const comment = reviewTitle
        ? `Title: ${reviewTitle}\n\n${reviewBody}`
        : reviewBody;

      // P-2 correction 2: the field is optional, so it is defaulted before
      // .trim() is reached — a bare data.productName.trim() throws when the
      // value is absent, and this function is also reached from tests and from
      // callers the compiler does not check.
      const productName = (data.productName ?? "").trim();

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(`${base}/api/feedback/review/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // Session 519 review, N-4: the FMS product lookup matches the
            // catalogue number (deskshare_product_id), the one the desktop
            // applications send, and never a slug. Send it as product_id when
            // the product has one; otherwise the slug, which the FMS falls back
            // to. product_slug always carries the slug.
            product_id: isCatalogueNumber(data.deskshareProductId)
              ? String(data.deskshareProductId)
              : data.productSlug,
            product_slug: data.productSlug,
            // P-2: the display name, so support staff stop translating slugs in
            // their heads on every review ticket. Only sent when we actually
            // have one — the FMS falls back to product_id when the field is
            // absent, and an empty string may defeat that fallback.
            ...(productName ? { product_name: productName } : {}),
            product_version: "web",
            user_name: data.reviewerName,
            email: data.email,
            rating: data.rating,
            comment,
            order_number: data.orderNumber ?? "",
            app_language: data.language ?? "en",
            operating_system: "Web",
          }),
          signal: controller.signal,
        });

        const parsed = await response.json().catch(() => ({}));
        if (!response.ok || parsed.success === false) {
          const err = new Error(parsed.message || `HTTP ${response.status}`);
          // A-6: Capture failures — raw fetch bypasses the shared ApiClient.
          Sentry.captureException(err, {
            tags: { api_method: "POST (review)" },
            contexts: {
              request: { url: `${base}/api/feedback/review/`, method: "POST" },
              response: { status: response.status },
            },
            extra: { status: response.status },
          });
          const statusErr = err as Error & { status?: number };
          statusErr.status = response.status;
          throw statusErr;
        }
        return { message: parsed.message, status: response.status };
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          const timeoutErr = new Error(
            "Review submission timed out. Please try again.",
          );
          Sentry.captureException(timeoutErr, {
            tags: { api_method: "POST (review)", timeout: true },
          });
          throw timeoutErr;
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    },
  },

  // Support (uninstall feedback and other app-level submissions)
  support: {
    // DD-04: submitUninstallFeedback moved from raw fetch to apiClient.postDirect —
    // consistent with all other form submissions; gets retry/timeout from ApiClient.
    submitUninstallFeedback: async (
      data: Record<string, string>,
    ): Promise<void> => {
      await apiClient.postDirect<{ message: string }>(
        "api/uninstall-feedback/",
        data,
      );
    },
  },

  // N-7 (Session 440): `pricing.getProductPrice` is gone. Its own note said it
  // "will be removed entirely when R-2 Part B (backend
  // currency_code/currency_symbol migration) is deployed", and R-2 Part B is
  // deployed — every cart row carries the currency.
  //
  // It fetched ${VITE_REACT_APP_API_DESKSHARE_URL}/general/get-product-price/,
  // and Api Deskshare publishes no such route: its urls.py registers health/,
  // textSpeakerLive/, dtranslator/ and admin/, and its handoff says "This
  // service holds no price ... There is not one reference to a price anywhere
  // in it". The production host answers today only because it is running an
  // older build of that project. Every call raised a Sentry error, twice per
  // visit to the Text Speaker Live purchase address.
  //
  // With it went the only reader of VITE_REACT_APP_API_DESKSHARE_URL, so that
  // variable is out of .env.example and api.deskshare.com is out of the
  // content policy's connect-src in nginx.conf.

  // Support forum (external forum API)
  forum: {
    getData: async <T>(path: string): Promise<T | null> => {
      // A-15/A-16: one accessor for the forum address. Reading the raw
      // variable here produced the string "undefined/api/..." on every
      // environment where it was unset, which is a request that always fails.
      const url = `${getForumUrl()}${path}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          Sentry.captureMessage(
            `Forum API error: ${response.status} for ${path}`,
            "warning",
          );
          return null;
        }
        return (await response.json()) as T;
      } catch (error) {
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { api_method: "GET (forum)" },
            contexts: { request: { url, method: "GET" } },
          },
        );
        return null;
      } finally {
        clearTimeout(timeoutId); // always clears — prevents timer leak when NodeBB is unreachable
      }
    },
  },

  // Authentication & cart status
  auth: {
    getShoppingCartLoginStatus: (): ShoppingCartStatus => {
      // T1: single source of truth — the website's own auth session.
      //
      // authClient.isAuthenticated() reads ds_session_token from localStorage,
      // which is origin-scoped and therefore cannot be set by the legacy store.
      //
      // History (do not re-add the cookie check without reading this):
      // Session 287 (FCR Pass 11, item 6.3) added a legacy cookie check here
      // (DeskShareUserCookie / __Secure-session_token) to fix the opposite
      // defect — website-auth users were shown "Sign In" because their session
      // lives in localStorage, not a cookie. That fix was correct, but the OR
      // meant a stale legacy cookie alone was enough to show "My Account" to
      // someone with no live session at all. Legacy store cookies persist for
      // weeks after a visit to store.deskshare.com and say nothing about the
      // current session.
      //
      // Nothing working is lost by dropping them: the legacy "My Account" path
      // led to a store that does not recognise website logins and redirected to
      // a legacy login screen (see T2).
      const isuser_loggedin = authClient.isAuthenticated();
      const is_user = isuser_loggedin;

      return { isuser_loggedin, is_user };
    },
  },

  // Generic product data endpoints (V-3: namespace was missing, causing TS2339)
  data: {
    /** Fetch product-specific data by path (e.g., "comparison/", "features/").
     *  Returns null on error — callers should handle gracefully. */
    fetch: async <T = unknown>(path: string): Promise<T | null> => {
      try {
        return await apiClient.get<T>(`product/${path}`);
      } catch {
        return null;
      }
    },
    /** Fetch common (non-product-scoped) data by path.
     *  Returns undefined on error — distinguishes from null product data. */
    fetchCommon: async <T = unknown>(path: string): Promise<T | undefined> => {
      try {
        return await apiClient.get<T>(path);
      } catch {
        return undefined;
      }
    },
  },

  // Cache management
  cache: {
    clear: () => apiClient.clearCache(),
    clearPath: (path: string) => apiClient.clearCacheForPath(path),
  },
};
